import { useRef, useState } from "react";
import { t } from "i18next";
import { X } from "lucide-react";
import { cn } from "@edusogno/lib/utils";
import { ExerciseType } from "./ExerciseBadge";
import { enqueueSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import { useCorrectExerciseMutation } from "@/data/exercises";
import { ExerciseTODO, NavigationState, QuestionItem } from "@/types";
import dayjs, { Dayjs } from "dayjs";
import Timer from "./Timer";
import routes from "@/config/routes";
import Button from "@edusogno/primitives/button.js";
import LoadingButton from "@edusogno/primitives/loading-button.js";
import NewAudioPlayer from "./NewAudioPlayer";
import { useQueryClient } from "@tanstack/react-query";
import API_ENDPOINTS from "@/data/client/endpoints";

type Props = {
  src?: string;
  type?: ExerciseType | string;
  onClose?: () => void;
  onSubmit?: () => void;
  exercise?: ExerciseTODO;
  isCorrection?: boolean;
  onViewEssayByTutor?: () => void;
};

const ActionBar = ({ type, src, onClose, isCorrection, exercise }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mutateAsync: correctExercise } = useCorrectExerciseMutation();
  const queryClient = useQueryClient();

  const startTime = useRef<null | Dayjs>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSubmissionConfirmation, setShowSubmissionConfirmation] =
    useState(false);

  const state = location.state as NavigationState;

  const { isCR, linkVideo } = state || {};

  function compareAnswers(
    correctAnswers: QuestionItem,
    userAnswers: Record<number, string>,
  ) {
    const incorrectAnswers: Record<
      number,
      { correct_answer: string; user_answer: string }
    > = {};

    const regex = /\(([^)]+)\)/;

    for (const [questionNo, question] of Object.entries(correctAnswers)) {
      const questionNumber = parseInt(questionNo);
      const correctAnswer = question.correct_answer ?? "";
      const userAnswer = userAnswers[questionNumber] ?? "";

      const match = correctAnswer.match(regex);
      let correctedAnswers: string[] = [];

      if (match) {
        correctedAnswers.push(
          correctAnswer.replace(match[0], match[1]).replace(/\s+/g, " ").trim(),
        );
        correctedAnswers.push(
          correctAnswer.replace(match[0], "").replace(/\s+/g, " ").trim(),
        );
      } else {
        correctedAnswers = correctAnswer.split("/").map((e) => e.trim());
      }

      const normalizedUserAnswer = userAnswer
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
      const isCorrect = correctedAnswers.some(
        (answer) => answer.toLowerCase() === normalizedUserAnswer,
      );

      if (!isCorrect) {
        incorrectAnswers[questionNumber] = {
          correct_answer: question.correct_answer,
          user_answer: userAnswer === "" ? "N/A" : userAnswer,
        };
      }
    }
    return incorrectAnswers;
  }

  const prepareSubmit = async () => {
    if (!exercise) return;
    const currentTime = dayjs();
    const currentDuration = dayjs.duration(
      currentTime.diff(startTime?.current),
    );
    const formattedDuration = dayjs
      .utc(currentDuration.asMilliseconds())
      .format("HH:mm:ss");

    setIsLoading(true);
    const form = document.getElementById("form-ex");
    if (!form) {
      setIsLoading(false);
      return;
    }
    const formInputs = [...form.querySelectorAll("input")];
    const formData = [...formInputs].filter((el) =>
      el.tagName.match(/^INPUT$/),
    );

    const userData: Record<number, string> = {};

    if (
      exercise.modality === "Listening" ||
      exercise.modality === "Reading" ||
      exercise.modality === "Grammar" ||
      exercise.modality === "Linguaskill"
    ) {
      formData.forEach((el, i) => {
        userData[i + 1] = el.value;
      });

      const userSubmit = compareAnswers(exercise.questions, userData);

      const submitData = {
        user_submit: JSON.stringify(userSubmit),
        token_ex: exercise.token,
        test_name: exercise.name,
        type: exercise.modality,
        timer: formattedDuration,
      };

      const response = await correctExercise({
        id: exercise.id,
        ...submitData,
      });

      if (response) {
        void queryClient.invalidateQueries([API_ENDPOINTS.USER_EXERCISES]);
        if (response[0].type !== "Writing") {
          //TODO: Fix this
          // const modality = exercise.modality?.toLowerCase();

          // if (modality == "linguaskill") {
          //   // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          //   const category: string | null = JSON.parse(exercise?.category)?.[0] || null;
          //   if (category) {
          //     navigate(`${routes.exercises}/correction/${category.toLowerCase()}/${exercise.token}`,
          //       {
          //         replace: true,
          //        },
          //     );
          //   }
          // } else {
          //   navigate(
          //     `${routes.exercises
          //     }/correction/${exercise.modality?.toLowerCase()}/${exercise.token}`,
          //     { replace: true },
          //   );
          // }

          navigate(routes.exercises, {
            replace: true,
            state: { tab: "done" },
          });
        } else {
          navigate(routes.exercises, {
            replace: true,
            state: { tab: "done" },
          });
        }
        navigate(-1);
      }
    }

    setIsLoading(false);
  };

  const prepareWriting = async () => {
    if (!exercise) return;
    setIsLoading(true);
    const formData = document.getElementById("form-ex");

    if (!formData) {
      setIsLoading(false);
      return;
    }

    const userData: string[] = [
      ...formData.querySelectorAll("textarea"),
      ...formData.querySelectorAll("input"),
    ].map((el) => el.value);

    const filtered = userData.filter((element) => {
      return element?.length > 10;
    });

    const submitData = {
      user_submit: JSON.stringify(filtered),
      raw_text_answer: formData.querySelectorAll("textarea")[0].value,
      token_ex: exercise.token,
      test_name: exercise.name,
      type: exercise.modality,
    };

    const response = await correctExercise({
      id: exercise?.id,
      ...submitData,
    });

    setIsLoading(false);

    if (response) {
      if (response[0].type !== "Writing") {
        navigate(
          `${routes.exercises}/correction/${exercise.modality?.toLowerCase()}/${
            exercise.token
          }`,
          { replace: true },
        );
      } else {
        navigate(routes.exercises, {
          replace: true,
          state: { tab: "todo" },
        });
      }
      navigate(-1);
    }
  };

  const handleSubmit = () => {
    if (exercise?.modality !== "Writing") {
      void prepareSubmit().then(() => {});
    } else {
      void prepareWriting().then(() => {});
    }
  };

  const handleRedirect = () => {
    if (linkVideo) {
      window.open(linkVideo, "_blank");
    } else {
      enqueueSnackbar(t("class_report.no_recording"), { variant: "error" });
    }
  };

  return (
    <div className="flex justify-end flex-row until-xl:w-full items-center xl:h-[50px] until-xl:justify-center gap-1 xl:gap-4">
      {showConfirmation && (
        <div className="flex xl:h-[50px] lg:h-[102px] flex-row gap-3 w-full justify-end items-center">
          <p className="text-xs font-medium text-white text-start until-xl:mr-auto xl:text-end md:text-xl xl:text-xs">
            {type === "Comprehension"
              ? t("exercises.close_message_comprehension")
              : t("exercises.close_message_all")}
          </p>
          <p></p>

          <div className="flex flex-row gap-1">
            <Button
              size="sm"
              color="green"
              onClick={() => setShowConfirmation(false)}
              className="rounded-[10px] [--bg:#FE9595] min-w-[45px] w-[45px]  h-[30px]"
            >
              No
            </Button>

            <Button
              size="sm"
              color="green"
              onClick={onClose}
              className="rounded-[10px] min-w-[45px] w-[45px]  h-[30px]"
            >
              Yes
            </Button>
          </div>
        </div>
      )}

      {showSubmissionConfirmation && (
        <div className="flex xl:h-[50px] h-[102px] flex-row gap-3 w-full justify-end items-center ">
          <p className="font-medium text-white xl:w-full text-start until-xl:mr-auto xl:text-end md:text-xl xl:text-xs ">
            {t("exercises.submit_message")}
          </p>

          <div className="flex flex-row gap-1">
            <Button
              size="sm"
              color="green"
              onClick={() => setShowSubmissionConfirmation(false)}
              className="rounded-[10px] [--bg:#FE9595] min-w-[45px] w-[45px]  h-[30px]"
            >
              No
            </Button>

            <LoadingButton
              size="sm"
              color="green"
              loading={isLoading}
              onClick={handleSubmit}
              className="rounded-[10px] min-w-[45px] w-[45px]  h-[30px]"
            >
              Yes
            </LoadingButton>
          </div>
        </div>
      )}

      <div
        className={cn(
          "flex flex-col w-full xl:w-auto xl:flex-row gap-4 items-center",
          {
            hidden: showConfirmation || showSubmissionConfirmation,
          },
        )}
      >
        {!isCorrection && !isCR && (
          <>
            {type === ExerciseType.LISTENING ? (
              <NewAudioPlayer src={src} restricted className="xl:w-[420px]" />
            ) : (
              <Timer
                onStart={(s) => {
                  startTime.current = s as Dayjs;
                }}
                run={true}
                duration={0}
              />
            )}

            <Button
              onClick={() => {
                setShowSubmissionConfirmation(true);
              }}
              size="sm"
              className={`rounded-[10px] min-w-[60px] max-w-full w-full xl:w-[100px] xl:h-[30px] ${
                type === ExerciseType.LISTENING ? "xl:mr-8" : ""
              } `}
            >
              Submit
            </Button>
          </>
        )}

        {/* CLASS REPORT */}
        {isCR && linkVideo && (
          <Button
            onClick={() => {
              handleRedirect();
            }}
            size="sm"
            className="rounded-[10px] px-3 max-w-full w-full xl:h-[30px]"
          >
            Watch Recording
          </Button>
        )}

        {isCorrection && type === ExerciseType.LISTENING && (
          <NewAudioPlayer src={src} className="xl:w-[540px]" />
        )}

        <Button
          onClick={() => {
            if (isCorrection || isCR) {
              if (onClose) onClose();
            } else {
              setShowConfirmation(true);
            }
          }}
          disabled={isLoading}
          variant="ghost"
          color="white"
          // className="w-fit until-xl:h-[22px] until-xl:absolute until-xl:top-0 until-xl:right-0 2xl:static xl:!top-4"
          className="w-fit until-xl:h-[22px] 2xl:static"
        >
          <X />
        </Button>
      </div>
    </div>
  );
};
export default ActionBar;
